 
import { Grid, Modal, Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  Add,
  Button,
  EditableTable,
  FileDropZone,
  FilePresent,
  OrDivider,
  PageTitleAndSubTitle,
  RadioButton,
  TextField,
  TypableDropdown,
  UploadedDocument,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import SvgClose from '@HomaHealth/ui-component-library/lib/_components/atoms/Icons/Close';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { scrollBarStyles } from '../../../core-utils/messages/CommonConstants';
import ReportCreateNewLabelField from '../../atoms/ReportCreateNewLabelField';
import { removeEmptyFields } from '../../../core-utils/helper-utils/riskRegisterHelper';
import { financialForensicsConstants } from '../../../core-utils/messages/financialForensicsConstants';
import { editableColumn } from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/EditableTable';
import { formatFileSize } from '../../../core-utils/helper-utils/TaskBoardHelper';
import { generateDateRanges } from '../../../core-utils/helper-utils/financialForensicsHelper';
import {
  EmployeeCountModalProps,
  TimeSeriesEntry,
  emptyEmployeeCountModalData,
} from '../../../pages/FinancialForensicsPage/types/EmployeeCount.types';
import { CountFrequency } from '../../../pages/FinancialForensicsPage/FinancialForensics.types';

export interface AddEmployeeCountModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
   
  handleSubmit: (data: any) => void;
  handleOpenSnackBar: any;
  startDate: string;
  endDate: string;
  sourceOfDataOptions: string[];
   
  updateFieldNamesForSourceOfData: (value: string, field: string) => void;
  dataFromProps?: EmployeeCountModalProps;
  handleCloseModal?: () => void;
}

interface DetailOption {
  name: CountFrequency;
  checked: boolean;
}

const DETAIL_OPTIONS: DetailOption[] = [
  { name: 'Monthly', checked: false },
  { name: 'Quarterly', checked: false },
  { name: 'Annually', checked: false },
];

const AddEmployeeCountModal: React.FC<AddEmployeeCountModalProps> = observer(
  ({
    open,
    setOpen,
    handleSubmit,
    startDate,
    endDate,
    sourceOfDataOptions,
    updateFieldNamesForSourceOfData,
    dataFromProps,
    handleCloseModal,
  }) => {
    const useStyles = makeStyles()({
      modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      paper: {
        width: '1203px',
        height: '760px',
        padding: '24px 28px 40px 32px',
        backgroundColor: patientAccessTheme.palette.status.white,
        border: 'none !important',
        position: 'relative',
      },
      button: { padding: '0px' },
      scroll: scrollBarStyles,
      flex: {
        display: 'flex',
        gap: '100px',
        flexWrap: 'wrap',
        alignItems: 'start',
        margin: '0px 16px 20px 0px',
      },
      service: {
        backgroundColor: patientAccessTheme.palette.grey[100],
        padding: '20px 20px 15px 20px',
        height: 'fit-content',
      },
      content: {
        display: 'flex',
        flexDirection: 'column',
        gap: '15px',
        overflowY: 'scroll',
        height: '588px',
      },
    });

    const { classes } = useStyles();

    const [detailOptions, setDetailOptions] = useState<DetailOption[]>(
      DETAIL_OPTIONS,
    );
    const [data, setData] = useState<EmployeeCountModalProps>(
      emptyEmployeeCountModalData(),
    );
    const [subscriptionType, setSubscriptionType] = useState('');
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

    const isCommonFieldsFilled =
      !!data.source &&
      data.details.length > 0 &&
      data.details.every((item) => item.type && item.values.length > 0);

    const handleClose = () => {
      setOpen(false);
      setData(emptyEmployeeCountModalData());
      handleCloseModal?.();
    };

    const handleDetailsOptionChange = (index: number) => {
      const updatedOptions = detailOptions.map((option, i) => ({
        ...option,
        checked: i === index,
      }));
      setDetailOptions(updatedOptions);

      const selectedOption = updatedOptions[index];
      if (!selectedOption.checked) {
        return;
      }

      const dateRanges: string[] = generateDateRanges(
        selectedOption.name,
        startDate,
        endDate,
      );

      const values: TimeSeriesEntry[] = dateRanges.map((dateRange) => ({
        dateRange,
        value: 0,
      }));

      setData((prev) => ({
        ...prev,
        detailsType: selectedOption.name,
        details: prev.details.map((detail) => ({ ...detail, values })),
      }));
    };

    const handleAddRow = () => {
      if (!subscriptionType) {
        return;
      }
      setData((prev) => ({
        ...prev,
        details: [...prev.details, { type: subscriptionType, values: [] }],
      }));
      setSubscriptionType('');
    };

    const handleTableDataChange = (updatedRows: any[]) => {
      const updatedDetails = updatedRows.map(({ type, ...rest }) => ({
        type,
        values: Object.entries(rest).map(([dateRange, value]) => ({
          dateRange,
          value: value as number,
        })),
      }));

      setData((prev) => ({ ...prev, details: updatedDetails }));
    };

    const handleSaveOrSubmit = () => {
      const cleanedData = removeEmptyFields(data);
      handleSubmit({ ...cleanedData, files: uploadedFiles });
      setOpen(false);
    };

    const getColumnsForTable = (): editableColumn[] => {
      const firstValues = data.details[0]?.values;
      if (!firstValues?.length) {
        return [];
      }

      const firstColumn: editableColumn = {
        key: 'type',
        label: 'Subscription Type',
        editable: false,
        width: '180px',
      };

      const dateRangeColumns: editableColumn[] = firstValues.map((entry) => ({
        key: entry.dateRange,
        label: entry.dateRange,
        editable: true,
        type: 'number',
      }));

      return [firstColumn, ...dateRangeColumns];
    };

    const getTableData = () =>
      data.details.map((detail) => {
        const row: Record<string, string | number | null> = {
          type: detail.type,
        };
        detail.values.forEach((entry) => {
          row[entry.dateRange] = entry.value;
        });
        return row;
      });

    useEffect(() => {
      if (!dataFromProps?.detailsType) {
        return;
      }

      const updatedOptions = detailOptions.map((opt) => ({
        ...opt,
        checked: opt.name === dataFromProps.detailsType,
      }));
      setDetailOptions(updatedOptions);

      const headers: string[] = generateDateRanges(
        dataFromProps.detailsType,
        startDate,
        endDate,
      );

      const normalizedDetails = dataFromProps.details.map((row) => ({
        type: row.type,
        values: headers.map((dateRange) => ({
          dateRange,
          value:
            row.values.find((v) => v.dateRange === dateRange)?.value ?? null,
        })),
      }));

      setData({ ...dataFromProps, details: normalizedDetails });
    }, [dataFromProps]);

    return (
      <Modal open={open} onClose={handleClose} className={classes.modal}>
        <Paper className={classes.paper}>
          <Grid
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '30px',
            }}
          >
            <PageTitleAndSubTitle
              title={financialForensicsConstants.addEmployeeCount}
              subTitle={''}
              subTitleStyles={{ width: '0px' }}
            />
            <SvgClose
              style={{ cursor: 'pointer' }}
              color={patientAccessTheme.palette.status.icon}
              onClick={handleClose}
            />
          </Grid>

          <Grid className={`${classes.scroll} ${classes.content}`}>
            <Grid className={`${classes.flex} ${classes.service}`}>
              <ReportCreateNewLabelField
                label={financialForensicsConstants.sourceOfData}
                component={
                  <TypableDropdown
                    dropDownOptions={sourceOfDataOptions}
                    dropDownValues={sourceOfDataOptions}
                    placeholder=""
                    value={data.source}
                    textFieldPlaceholder="Add Source of Data"
                    handleChange={(event: any) =>
                      setData((prev) => ({
                        ...prev,
                        source: event.target.value,
                      }))
                    }
                    handleInputChange={(value: string) => {
                      updateFieldNamesForSourceOfData(
                        value,
                        'source_of_data_employees',
                      );
                      setData((prev) => ({ ...prev, source: value }));
                    }}
                    error={!data.source}
                    width="500px"
                  />
                }
              />
            </Grid>

            {data.details.length > 0 && (
              <>
                <Typography
                  textColor={patientAccessTheme.palette.text.highemphasis}
                  fontSize="12px"
                  lineHeight="12px"
                  fontType="medium"
                >
                  {financialForensicsConstants.countQuestion}
                </Typography>

                <div
                  style={{ display: 'flex', gap: '20px', marginBottom: '10px' }}
                >
                  {detailOptions.map((option, index) => (
                    <label
                      key={option.name}
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      <RadioButton
                        color={patientAccessTheme.palette.primary[400]}
                        checked={option.checked}
                        handleChange={() => handleDetailsOptionChange(index)}
                      />
                      <span style={{ marginLeft: '6px' }}>
                        <Typography
                          textColor={
                            patientAccessTheme.palette.text.highemphasis
                          }
                          fontSize="12px"
                          lineHeight="12px"
                          fontType="semiBold"
                        >
                          {option.name}
                        </Typography>
                      </span>
                    </label>
                  ))}
                </div>

                <Grid style={{ marginRight: '20px' }}>
                  <EditableTable
                    data={getTableData()}
                    columns={getColumnsForTable()}
                    onChange={handleTableDataChange}
                    containerStyles={{
                      overflow: 'visible',
                      height: 'auto',
                      overflowY: 'visible',
                      width: '100%',
                    }}
                    showDeleteIcon={true}
                    deleteIconColumnIndex={0}
                  />
                </Grid>
              </>
            )}

            <Grid style={{ marginTop: '20px' }}>
              <TextField
                width="1008px"
                height="34px"
                placeholder={financialForensicsConstants.subscriptionType}
                onChange={(event: any) =>
                  setSubscriptionType(event.target.value)
                }
                value={subscriptionType}
                placeholderStyle={{ fontSize: '16px' }}
              />
              <Button
                label={
                  <>
                    <Typography
                      textColor={patientAccessTheme.palette.status.white}
                      fontSize="12px"
                      lineHeight="12px"
                      fontType="medium"
                    >
                      {financialForensicsConstants.addRow}
                    </Typography>
                    <Add stroke="white" />
                  </>
                }
                variant="contained"
                color="primary"
                height="34px"
                width="100px"
                borderRadius="2px"
                className={classes.button}
                onClick={handleAddRow}
                disabled={!subscriptionType}
              />
            </Grid>

            <OrDivider />

            <Typography
              textColor={patientAccessTheme.palette.text.highemphasis}
              fontSize="12px"
              lineHeight="12px"
              fontType="medium"
            >
              {financialForensicsConstants.upload}
            </Typography>
            <FileDropZone
              filesSupported={['.pdf', '.doc', '.docx']}
              uploadedFiles={uploadedFiles}
              setUploadedFiles={setUploadedFiles}
              isMultiple={true}
              dragDropAreaStyles={{ marginRight: '20px', width: '98%' }}
            />
            {uploadedFiles.map((file, index) => (
              <UploadedDocument
                key={index}
                width="592px"
                name={file.name}
                truncateWidth="420px"
                size={formatFileSize(file.size)}
                startIcon={
                  <FilePresent
                    stroke={patientAccessTheme.palette.primary[500]}
                    fill={patientAccessTheme.palette.primary[500]}
                  />
                }
                handleDelete={() =>
                  setUploadedFiles((prev) => prev.filter((_, i) => i !== index))
                }
              />
            ))}
          </Grid>

          <Grid
            style={{
              display: 'flex',
              justifyContent: 'right',
              margin: '0px 40px 40px 0px',
              position: 'absolute',
              right: '0px',
              bottom: '0px',
            }}
          >
            <Button
              label={
                <Typography
                  textColor={patientAccessTheme.palette.primary.main}
                  fontSize="12px"
                  lineHeight="12px"
                  fontType="medium"
                >
                  {financialForensicsConstants.Save_update}
                </Typography>
              }
              variant="outlined"
              height="36px"
              width="180px"
              styleProps={{ marginRight: '25px' }}
              borderRadius="4px"
              className={classes.button}
              onClick={handleSaveOrSubmit}
            />
            <Button
              label={
                <Typography
                  textColor={patientAccessTheme.palette.status.white}
                  fontSize="12px"
                  lineHeight="12px"
                  fontType="medium"
                >
                  {financialForensicsConstants.submit}
                </Typography>
              }
              variant="contained"
              color="primary"
              height="36px"
              width="180px"
              borderRadius="4px"
              className={classes.button}
              onClick={handleSaveOrSubmit}
              disabled={!isCommonFieldsFilled}
            />
          </Grid>
        </Paper>
      </Modal>
    );
  },
);

export default AddEmployeeCountModal;
